import type { ChildProfile, ContentNode, TrustEvent } from "../types";

export const childrenSeed: ChildProfile[] = [
  {
    id: "ethan",
    name: "Ethan",
    age: 7,
    yearGroup: "Year 2",
    ageBand: "5-7",
    avatar: "E",
    weeklyMinutes: 265,
    focusScore: 84,
    frustrationSignals: 1,
    sessionLimitMinutes: 18,
    currentPath: "Phase 5 phonics and early multiplication",
    preferences: {
      format: "story problem",
      theme: "Harbour puzzles",
      bestTime: "After school"
    },
    skills: [
      {
        id: "blend-ai",
        subject: "Reading",
        strand: "Phonics",
        label: "Blend split digraphs in real words",
        mastery: "secure",
        accuracy: 0.84,
        stability: 0.76,
        lastPractised: "Today",
        supportLevel: "low",
        ageRespectfulTheme: "Harbour maps"
      },
      {
        id: "pseudo-words",
        subject: "Reading",
        strand: "Phonics",
        label: "Decode pseudo-words without guessing",
        mastery: "learning",
        accuracy: 0.68,
        stability: 0.52,
        lastPractised: "Yesterday",
        supportLevel: "medium",
        ageRespectfulTheme: "Secret labels"
      },
      {
        id: "arrays",
        subject: "Maths",
        strand: "Multiplication",
        label: "Use arrays for 2, 5, and 10 times tables",
        mastery: "review due",
        accuracy: 0.77,
        stability: 0.58,
        lastPractised: "3 days ago",
        supportLevel: "medium",
        ageRespectfulTheme: "Market stalls"
      }
    ]
  },
  {
    id: "amelia",
    name: "Amelia",
    age: 5,
    yearGroup: "Reception",
    ageBand: "3-5",
    avatar: "A",
    weeklyMinutes: 132,
    focusScore: 78,
    frustrationSignals: 0,
    sessionLimitMinutes: 12,
    currentPath: "Rhyme, oral retelling, and quantities to 8",
    preferences: {
      format: "shared story",
      theme: "Garden songs",
      bestTime: "Weekend morning"
    },
    skills: [
      {
        id: "rhyme-match",
        subject: "Reading",
        strand: "Phonological awareness",
        label: "Hear and match rhyming words",
        mastery: "secure",
        accuracy: 0.91,
        stability: 0.82,
        lastPractised: "Today",
        supportLevel: "low",
        ageRespectfulTheme: "Garden songs"
      },
      {
        id: "quantity-6",
        subject: "Maths",
        strand: "Number sense",
        label: "Show 6 in two different ways",
        mastery: "learning",
        accuracy: 0.72,
        stability: 0.61,
        lastPractised: "Today",
        supportLevel: "medium",
        ageRespectfulTheme: "Picnic plates"
      }
    ]
  },
  {
    id: "noah",
    name: "Noah",
    age: 11,
    yearGroup: "Year 7",
    ageBand: "11-13",
    avatar: "N",
    weeklyMinutes: 188,
    focusScore: 81,
    frustrationSignals: 2,
    sessionLimitMinutes: 22,
    currentPath: "KS3 transition reading and proportional reasoning",
    preferences: {
      format: "challenge",
      theme: "Knowledge quest",
      bestTime: "Early evening"
    },
    skills: [
      {
        id: "multi-syllable",
        subject: "Reading",
        strand: "Fluency",
        label: "Break complex multisyllabic words into parts",
        mastery: "learning",
        accuracy: 0.64,
        stability: 0.48,
        lastPractised: "Yesterday",
        supportLevel: "medium",
        ageRespectfulTheme: "Archive investigation"
      },
      {
        id: "ratio-table",
        subject: "Maths",
        strand: "Proportional reasoning",
        label: "Use ratio tables to scale recipes and maps",
        mastery: "secure",
        accuracy: 0.82,
        stability: 0.73,
        lastPractised: "2 days ago",
        supportLevel: "low",
        ageRespectfulTheme: "Design studio"
      }
    ]
  }
];

export const contentNodes: ContentNode[] = [
  {
    id: "boat-split-digraph",
    subject: "Reading",
    strand: "Phonics",
    domain: "Word reading",
    skill: "blend-ai",
    prerequisites: ["letter-sound-security", "blend-cvc"],
    ageBand: "5-7",
    minutes: 8,
    accessibilityTags: ["read-aloud", "reduced-motion", "captions", "large-targets"],
    instructionScript: "We look for the split digraph, say the sound, blend the word, then check it in a sentence.",
    guidedPractice: ["sail", "paint", "train"],
    independentPractice: ["rain", "snail", "mail"],
    misconceptionTags: ["guesses from first letter", "misses vowel team"],
    successRule: "80% accuracy across two short sessions with lower support.",
    parentExtension: "Find three things at home with the /ai/ sound and say them in a sentence.",
    status: "ready",
    confusionRate: 0.18,
    replayRate: 0.24
  },
  {
    id: "six-two-ways",
    subject: "Maths",
    strand: "Number sense",
    domain: "Composition",
    skill: "quantity-6",
    prerequisites: ["subitise-5", "count-to-8"],
    ageBand: "3-5",
    minutes: 6,
    accessibilityTags: ["adult-co-use", "audio", "touch"],
    instructionScript: "Six can be a full five-frame and one more. Six can also be three and three.",
    guidedPractice: ["5 and 1", "4 and 2"],
    independentPractice: ["3 and 3", "2 and 4"],
    misconceptionTags: ["counts objects twice", "does not conserve quantity"],
    successRule: "Shows six in two ways without recounting every object.",
    parentExtension: "Build 6 using spoons, blocks, or socks in two different ways.",
    status: "ready",
    confusionRate: 0.13,
    replayRate: 0.19
  },
  {
    id: "ratio-recipe",
    subject: "Maths",
    strand: "Proportional reasoning",
    domain: "Ratio",
    skill: "ratio-table",
    prerequisites: ["multiplication-facts", "fraction-equivalence"],
    ageBand: "11-13",
    minutes: 12,
    accessibilityTags: ["age-respectful", "worked-example", "keyboard"],
    instructionScript: "A ratio table keeps the relationship the same while the amounts change.",
    guidedPractice: ["Scale 2:3 to 4:6", "Scale 5:8 to 10:16"],
    independentPractice: ["Scale a recipe for 2 people to 6 people"],
    misconceptionTags: ["adds instead of multiplies", "changes only one column"],
    successRule: "Explains the multiplier and applies it to both quantities.",
    parentExtension: "Double or halve a real recipe together and check every ingredient.",
    status: "review",
    confusionRate: 0.31,
    replayRate: 0.16
  },
  {
    id: "word-parts-investigation",
    subject: "Reading",
    strand: "Fluency",
    domain: "Morphology",
    skill: "multi-syllable",
    prerequisites: ["syllable-types", "common-prefixes"],
    ageBand: "11-13",
    minutes: 10,
    accessibilityTags: ["age-respectful", "captions", "read-aloud"],
    instructionScript: "Long words become manageable when we mark prefixes, roots, suffixes, and syllable breaks.",
    guidedPractice: ["unreliable", "transportation", "misunderstanding"],
    independentPractice: ["reconsideration", "environmental"],
    misconceptionTags: ["skips middle syllable", "reads suffix as separate word"],
    successRule: "Accurately reads and explains word parts in new subject vocabulary.",
    parentExtension: "Choose a word from a book or article and find its root or prefix together.",
    status: "draft",
    confusionRate: 0.22,
    replayRate: 0.27
  }
];

export const trustEvents: TrustEvent[] = [
  {
    id: "privacy-ok",
    label: "Privacy log checked",
    detail: "No third-party tracking, ads, open chat, or child external links detected.",
    level: "ok",
    time: "Today 09:15"
  },
  {
    id: "session-age",
    label: "Session length within limit",
    detail: "Ages 3-5 sessions stayed below the configured 12 minute cap.",
    level: "ok",
    time: "Today 08:42"
  },
  {
    id: "frustration-watch",
    label: "Support need flagged",
    detail: "Noah replayed multisyllable word audio often; parent report suggests a short decoding repair.",
    level: "watch",
    time: "Yesterday 18:07"
  }
];

export const ageBandPlan = [
  {
    band: "3-5",
    title: "Guided play",
    copy: "Rhyme, story, vocabulary, subitising, patterns, and adult co-use.",
    theme: "Garden songs"
  },
  {
    band: "5-7",
    title: "Systematic foundations",
    copy: "Synthetic phonics, decodable reading, number bonds, arrays, and small-step maths.",
    theme: "Harbour puzzles"
  },
  {
    band: "7-9",
    title: "Fluency and meaning",
    copy: "Vocabulary, morphology, multiplication, fractions foundations, and problem structures.",
    theme: "Workshop quests"
  },
  {
    band: "9-11",
    title: "Knowledge building",
    copy: "Comprehension, inference, written methods, fractions, decimals, and multi-step reasoning.",
    theme: "Library expeditions"
  },
  {
    band: "11-13",
    title: "KS3 bridge",
    copy: "Disciplinary reading, age-respectful catch-up, algebraic thinking, statistics, and ratio.",
    theme: "Archive investigation"
  }
];
